import React, { useState, useReducer, useEffect, createRef } from 'react';
import cx from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { motion, AnimatePresence } from 'framer-motion';
import {
  Button,
  Pagination,
  Icon as SemanticIcon,
  Input,
  Select,
  Grid,
  Container,
} from 'semantic-ui-react';
import moment from 'moment';

import { Icon } from '@plone/volto/components';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getSearchFiltersEvents } from '@redturtle/iovisit-theme/actions';
import {
  VariationWrapper,
  DateRangeWidget,
} from '@redturtle/iovisit-theme/components';
import Card from '@redturtle/iovisit-theme/components/Blocks/Listing/CardsTemplate/Card';

import config from '@plone/registry';

import {
  DEFAULT_B_SIZE,
  FILTERS_ACTIONS,
  getDefaultStartEndDate,
  getFiltersReducer,
  getFilters,
  getLocalizedVocabulary,
  datesForDisplay,
} from '@redturtle/iovisit-theme/components/Blocks/SearchEvents/searchEventsUtils';

const messages = defineMessages({
  eventSearchText: {
    id: 'event_search_text',
    defaultMessage: 'Cerca per parola chiave',
  },
  eventSearchSubject: {
    id: 'event_search_subject',
    defaultMessage: 'Scegli la categoria',
  },
  eventSearchCity: {
    id: 'event_search_city',
    defaultMessage: 'Scegli la località',
  },
  eventSearchStartDate: {
    id: 'event_search_startDate',
    defaultMessage: 'Data inizio',
  },
  eventSearchEndDate: {
    id: 'event_search_endDate',
    defaultMessage: 'Data fine',
  },
  eventSearchEvents: {
    id: 'event_search_events',
    defaultMessage: 'Eventi',
  },
  eventSearchForText: {
    id: 'event_search_for',
    defaultMessage: 'contenenti',
  },
  eventSearchInSubject: {
    id: 'event_search_in',
    defaultMessage: 'in',
  },

  eventSearchFromDate: {
    id: 'event_search_fromdate',
    defaultMessage: 'dal',
  },
  eventSearchToDate: {
    id: 'event_search_todate',
    defaultMessage: 'al',
  },
  clearText: {
    id: 'clear_text',
    defaultMessage: 'Cancella',
  },
  no_results_found: {
    id: 'no_results_found',
    defaultMessage: 'Nessun risultato',
  },
  results_found: {
    id: 'results_found',
    defaultMessage: 'risultati',
  },
});

const Body = ({ data, isEditMode, onChangeBlock }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const resultsRef = createRef();
  moment.locale(intl.locale);
  const pathname = config.settings.isMultilingual ? '/' + intl.locale : '/'; //flattenToAppURL(location?.pathname).replace('/edit', '');

  /* ********* STATE ********* */
  const [b_size, setB_size] = useState(DEFAULT_B_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [lastSubrequest, setLastSubrequest] = useState(null);
  const defaultDates = getDefaultStartEndDate(data.whichEvents);
  const [defaultStartDate, setDefaultStartDate] = useState(
    defaultDates.startDate,
  );
  const [defaultEndDate, setDefaultEndDate] = useState(defaultDates.endDate);

  const citiesVocabularyName = config.settings.iovisit.eventsCitiesVocabulary;
  const vocabLocale = config.settings.isMultilingual ? intl.locale : null;

  const initialFiltersState = {
    date: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
    text: '',
    subject: null,
    city: null,

    changedFilters: data.whichEvents != null && data.whichEvents !== 'none',
  };

  /* ********* SELECTOR ********* */
  const querystringResults = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[lastSubrequest];
  });

  const items = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[lastSubrequest]?.items ?? [];
  });

  const loading = useSelector((state) => {
    return (
      state.querystringsearch?.subrequests?.[lastSubrequest]?.loading || false
    );
  });

  const filters_vocabulary = useSelector((state) => {
    return state.searchFiltersEvents || {};
  });

  const cities = useSelector((state) =>
    citiesVocabularyName
      ? state.vocabularies?.[citiesVocabularyName]?.items
      : null,
  );

  const citiesOptions = cities?.map((c) => ({
    key: c.value,
    value: c.label,
    text: c.label,
  }));

  /* ********* REDUCERS ********* */
  const filtersReducer = getFiltersReducer(initialFiltersState);
  const [{ text, subject, date, city, changedFilters }, dispatchFilter] =
    useReducer(filtersReducer, initialFiltersState);

  const getFiltersParam = () => {
    return getFilters(data.querystring?.query ?? data.query, {
      text,
      subject,
      city,
      date,
    });
  };
  const loadFilters = () => {
    dispatch(getSearchFiltersEvents(pathname, getFiltersParam()));
  };

  /* ********* EFFECTS ********* */

  const resetResults = () => {
    dispatch({
      type: 'RESET_QUERYSTRING_RESULTS',
      subrequest: lastSubrequest,
    });
  };

  useEffect(() => {
    if (data.whichEvents === 'none') {
      resetResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.whichEvents]);

  //get filters on changing querystring, default values, and on change lang
  useEffect(() => {
    if (!filters_vocabulary.loading) {
      loadFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStartDate, defaultEndDate, data.querystring, data, intl.locale]);

  useEffect(() => {
    if (citiesVocabularyName) {
      dispatch(
        getLocalizedVocabulary({
          vocabNameOrURL: citiesVocabularyName,
          size: 1000,
          locale: vocabLocale,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, citiesVocabularyName, vocabLocale]);

  const emptyFilters =
    (text || '')?.length === 0 &&
    (subject || '').length === 0 &&
    (city || '').length === 0 &&
    !date.startDate &&
    !date.endDate;

  useEffect(() => {
    if (changedFilters) {
      if (data.whichEvents === 'none' && emptyFilters) {
        resetResults();
      } else {
        doRequest(1);
      }

      loadFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    text,
    subject,
    date,
    city,
    changedFilters,
    b_size,
    data.sort_on,
    data.sort_order_boolean,
  ]);

  useEffect(() => {
    setB_size(data.b_size || DEFAULT_B_SIZE);
  }, [data.b_size]);

  // Se cambia il tipo di wichEvents o i criteri impostati dalla sidebar (data.querystring), resetto lo stato dei filtri
  useEffect(() => {
    let defaults = getDefaultStartEndDate(data.whichEvents);
    setDefaultStartDate(defaults.startDate);
    setDefaultEndDate(defaults.endDate);

    dispatchFilter({ type: FILTERS_ACTIONS.RESET });
  }, [data.whichEvents, data.querystring]);

  /* ********* FILTERS ********* */
  const subjects = filters_vocabulary?.data?.Subject?.map((subj) => ({
    key: subj,
    text: subj,
    value: subj,
  }));

  //FILTERSSTRINGS
  let filtersStrings = [];

  if (date.startDate && date.endDate) {
    const { sameYear, sameMonth, mStart, mEnd } = datesForDisplay(
      date.startDate,
      date.endDate,
    );

    filtersStrings.push(
      ` ${intl.formatMessage(messages.eventSearchFromDate)} ${mStart.format(
        sameMonth && sameYear ? 'D' : sameYear ? 'D MMMM' : 'LL',
      )} ${intl.formatMessage(messages.eventSearchToDate)} ${mEnd.format(
        'LL',
      )}`,
    );
  }

  if (subject) {
    filtersStrings.push(
      ` ${intl.formatMessage(messages.eventSearchInSubject)} '${subject}'`,
    );
  }

  // if the block's own query already presets a city, lock the filter on it:
  // a single preset city becomes the fixed value, multiple presets become
  // the only selectable options
  const baseQsCities =
    data?.query?.filter((el) => el.i === 'city')?.[0]?.v ?? null;

  const baseQsCity = baseQsCities?.length === 1 ? baseQsCities?.[0] : null;

  const baseQsCitiesOptions =
    baseQsCities?.length > 1
      ? baseQsCities.map((c) => ({
          key: citiesOptions?.find((e) => e.value === c)?.key ?? c,
          value: c,
          text: c,
        }))
      : null;

  /* ********* FN ********* */
  const doRequest = (page = currentPage) => {
    const filters = getFiltersParam();
    const subrequestId = new Date().getTime();
    setLastSubrequest(subrequestId);

    dispatch(
      getQueryStringResults(
        pathname,
        {
          fullobjects: 1,
          query: filters,
          b_size: b_size,
          sort_on: data.sort_on ?? null,
          sort_order: data.sort_order_boolean ? 'descending' : 'ascending',
        },
        subrequestId,
        page,
      ),
    );
  };

  const handleQueryPaginationChange = (e, { activePage }) => {
    resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    const current = activePage ?? 1;

    setCurrentPage(current);

    doRequest(current);
  };

  return (
    <div ref={resultsRef} className="iv--search-block-wrapper">
      <div
        className={cx('iv--search-filters search_type_event', {
          'small-filters': citiesVocabularyName && citiesOptions?.length > 0,
        })}
      >
        <div className="iv--filter">
          <div className="iv--search-filter-text">
            <Input
              placeholder={intl.formatMessage(messages.eventSearchText)}
              aria-label={intl.formatMessage(messages.eventSearchText)}
              value={text}
              className={text?.length > 0 ? 'has-value' : ''}
              icon
              onChange={(e, data) => {
                dispatchFilter({
                  type: FILTERS_ACTIONS.TEXT,
                  value: data.value ?? '',
                });
              }}
              aria-controls="search-results-region-events"
            >
              <input />
              {text.length > 0 && (
                <Button
                  inverted
                  icon
                  title={intl.formatMessage(messages.clearText)}
                  aria-label={intl.formatMessage(messages.clearText)}
                  onClick={() =>
                    dispatchFilter({
                      type: FILTERS_ACTIONS.TEXT,
                      value: '',
                    })
                  }
                >
                  <SemanticIcon name="delete" />
                </Button>
              )}
            </Input>
          </div>
        </div>
        <div
          className="iv--filter"
          aria-controls="search-results-region-events"
        >
          <DateRangeWidget
            value={date}
            onChange={(id, date) => {
              dispatchFilter({
                type: FILTERS_ACTIONS.DATE,
                value: { startDate: date.start, endDate: date.end },
              });
            }}
          />
        </div>
        {citiesVocabularyName && citiesOptions?.length > 0 && (
          <div className="iv--filter">
            <Select
              placeholder={intl.formatMessage(messages.eventSearchCity)}
              aria-label={intl.formatMessage(messages.eventSearchCity)}
              className="iv--search-filter-select"
              options={baseQsCitiesOptions ?? citiesOptions}
              value={baseQsCity ?? city}
              clearable
              onChange={(e, data) => {
                dispatchFilter({
                  type: FILTERS_ACTIONS.CITY,
                  value: data.value,
                });
              }}
              aria-controls="search-results-region-events"
              disabled={!!baseQsCity}
            />
          </div>
        )}
        {subjects?.length > 0 && (
          <div className="iv--filter">
            <Select
              placeholder={intl.formatMessage(messages.eventSearchSubject)}
              aria-label={intl.formatMessage(messages.eventSearchSubject)}
              className="iv--search-filter-select"
              options={subjects}
              value={subject}
              clearable
              onChange={(e, data) => {
                dispatchFilter({
                  type: FILTERS_ACTIONS.SUBJECT,
                  value: data.value,
                });
              }}
              aria-controls="search-results-region-events"
            />
          </div>
        )}
      </div>

      {/* mostra i risultati solamente dopo che l'utente ha inserito dei filtri (changedFilters) */}
      {loading && changedFilters ? (
        <Container textAlign="center" className="loader-container">
          <SemanticIcon loading name="spinner" size="big" />
        </Container>
      ) : (
        <div id="search-results-region-events">
          {changedFilters && !(emptyFilters && data.whichEvents === 'none') && (
            <div className="iv--results-count">
              {querystringResults?.total > 0
                ? `${querystringResults?.total} ${intl.formatMessage(
                    messages.results_found,
                  )}`
                : intl.formatMessage(messages.no_results_found)}{' '}
            </div>
          )}

          {items?.length > 0 && (
            <div className="block listing">
              <VariationWrapper items={items} variation="cards">
                <Grid stackable columns={3} verticalAlign="top">
                  <AnimatePresence initial={true}>
                    {items.map((item) => (
                      <motion.div
                        key={item['@id']}
                        className="column"
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.7, opacity: 0 }}
                      >
                        <Card item={item} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </Grid>
              </VariationWrapper>
              {querystringResults.total > b_size && (
                <div className="pagination-wrapper">
                  <Pagination
                    activePage={currentPage}
                    totalPages={Math.ceil(querystringResults.total / b_size)}
                    onPageChange={handleQueryPaginationChange}
                    firstItem={null}
                    lastItem={null}
                    prevItem={{
                      content: <Icon name={paginationLeftSVG} size="18px" />,
                      icon: true,
                      'aria-disabled': !querystringResults.batching.prev,
                      className: !querystringResults.batching.prev
                        ? 'disabled'
                        : null,
                    }}
                    nextItem={{
                      content: <Icon name={paginationRightSVG} size="18px" />,
                      icon: true,
                      'aria-disabled': !querystringResults.batching.next,
                      className: !querystringResults.batching.next
                        ? 'disabled'
                        : null,
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Body;
